import { useEffect, useState } from 'react'
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Image,
  Switch,
  message,
  Space,
  Row,
  Col,
  Table,
  Modal,
  Select,
  InputNumber,
  Tabs,
  Tag,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import type { UploadProps } from 'antd/es/upload'
import type { ColumnsType } from 'antd/es/table'
import { confirmDelete, confirmStatusChange } from '../../hooks/useConfirm'
import { homeConfigAPI, uploadAPI, API_BASE_URL, ASSETS_BASE_URL } from '../../api'

const { TextArea } = Input
const { Option } = Select

interface Banner {
  id: number
  imageUrl: string
  linkType: 'product' | 'category' | 'page' | 'none'
  linkValue: string
  sort: number
  status: boolean
}

interface Notice {
  id: number
  content: string
  sort: number
  status: boolean
}

interface RecommendProduct {
  id: number
  productId: number
  productName: string
  productImage: string
  price: number
  sort: number
  status: boolean
}


// Helper: ensure image URL has full API domain
const getImageUrl = (path: string) => {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) return path
  return `${ASSETS_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`
}

const HomeConfig = () => {
  const [activeTab, setActiveTab] = useState('banner')
  const [loading, setLoading] = useState(false)

  // 轮播图相关
  const [banners, setBanners] = useState<Banner[]>([])
  const [bannerModalVisible, setBannerModalVisible] = useState(false)
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null)
  const [bannerForm] = Form.useForm()
  const [bannerSubmitting, setBannerSubmitting] = useState(false)

  // 公告相关
  const [notices, setNotices] = useState<Notice[]>([])
  const [noticeModalVisible, setNoticeModalVisible] = useState(false)
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null)
  const [noticeForm] = Form.useForm()
  const [noticeSubmitting, setNoticeSubmitting] = useState(false)

  // 推荐商品相关 (自动显示销量前6)
  const [recommendProducts, setRecommendProducts] = useState<RecommendProduct[]>([])

  // 基本设置
  const [settings, setSettings] = useState({
    customerServicePhone: '400-888-8888',
    workTime: '周一至周五 9:00-18:00',
    maintenanceMode: false,
    maintenanceMessage: '系统维护中，请稍后再试',
  })
  const [settingsForm] = Form.useForm()
  const [settingsSaving, setSettingsSaving] = useState(false)

  useEffect(() => {
    fetchAllData()
  }, [])

  const fetchAllData = () => {
    fetchBanners()
    fetchNotices()
    fetchRecommendProducts()
    fetchSettings()
  }

  const fetchBanners = async () => {
    try {
      const res = await homeConfigAPI.getBanners()
      if (res.code === 200) {
        setBanners(res.data || [])
      }
    } catch (error) {
      setBanners([])
    }
  }

  const fetchNotices = async () => {
    try {
      const res = await homeConfigAPI.getNotices()
      if (res.code === 200) {
        setNotices(res.data || [])
      }
    } catch (error) {
      setNotices([])
    }
  }

  const fetchRecommendProducts = async () => {
    try {
      const { default: request } = await import('../../utils/request')
      const res = await request.get('/products', { params: { pageSize: 100, status: 'active' } })
      if (res.code === 200) {
        const productList = res.data.list || res.data || []
        // Sort by totalSales descending, take top 6
        const sorted = productList.sort((a: any, b: any) => (b.totalSales || b.sales || 0) - (a.totalSales || a.sales || 0)).slice(0, 6)
        setRecommendProducts(sorted.map((p: any, idx: number) => ({
          id: p.id,
          productId: p.id,
          productName: p.name || '',
          productImage: p.image || p.images?.[0] || '',
          price: p.price || 0,
          sort: idx + 1,
          status: true,
        })))
      }
    } catch (error) {
      setRecommendProducts([])
    }
  }



  const fetchSettings = async () => {
    try {
      const res = await homeConfigAPI.getConfig()
      if (res.code === 200 && res.data) {
        const s = res.data
        setSettings(s)
        settingsForm.setFieldsValue(s)
      }
    } catch (error) {
      settingsForm.setFieldsValue(settings)
    }
  }

  // ========== 轮播图管理 ==========
  const handleAddBanner = () => {
    setEditingBanner(null)
    bannerForm.resetFields()
    bannerForm.setFieldsValue({ linkType: 'none', sort: banners.length + 1, status: true })
    setBannerModalVisible(true)
  }

  const handleEditBanner = (record: Banner) => {
    setEditingBanner(record)
    bannerForm.setFieldsValue(record)
    setBannerModalVisible(true)
  }

  const handleDeleteBanner = (record: Banner) => {
    confirmDelete(`轮播图 #${record.id}`, '首页配置', async () => {
      const res = await homeConfigAPI.deleteBanner(record.id)
      if (res.code === 200) {
        setBanners(banners.filter(b => b.id !== record.id))
        message.success('删除成功')
        fetchBanners()
      }
    })
  }

  const handleToggleBannerStatus = (record: Banner, status: boolean) => {
    confirmStatusChange(`轮播图 #${record.id}`, status, '首页配置', async () => {
      setBanners(banners.map(b => b.id === record.id ? { ...b, status } : b))
    })
  }

  const handleMoveBanner = (id: number, direction: 'up' | 'down') => {
    const index = banners.findIndex(b => b.id === id)
    if (direction === 'up' && index > 0) {
      const newBanners = [...banners]
      ;[newBanners[index - 1], newBanners[index]] = [newBanners[index], newBanners[index - 1]]
      setBanners(newBanners.map((b, i) => ({ ...b, sort: i + 1 })))
    } else if (direction === 'down' && index < banners.length - 1) {
      const newBanners = [...banners]
      ;[newBanners[index], newBanners[index + 1]] = [newBanners[index + 1], newBanners[index]]
      setBanners(newBanners.map((b, i) => ({ ...b, sort: i + 1 })))
    }
  }

  const handleBannerSubmit = async () => {
    try {
      const values = await bannerForm.validateFields()
      setBannerSubmitting(true)

      if (editingBanner) {
        const res = await homeConfigAPI.updateBanner(editingBanner.id, values)
        if (res.code === 200) {
          setBanners(banners.map(b => b.id === editingBanner.id ? { ...b, ...values } : b))
          message.success('更新成功')
        }
      } else {
        const res = await homeConfigAPI.createBanner(values)
        if (res.code === 200) {
          setBanners([...banners, res.data])
          message.success('添加成功')
        }
      }
      setBannerModalVisible(false)
      fetchBanners()
    } catch (error) {
      // 错误统一处理
    } finally {
      setBannerSubmitting(false)
    }
  }

  const handleBannerUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options
    try {
      const res = await uploadAPI.uploadImage(file as File)
      if (res.code === 200) {
        const url = res.data.url || res.data.path || res.data
        const fullUrl = typeof url === 'string' && !url.startsWith('http') ? `${ASSETS_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}` : url
        bannerForm.setFieldValue('imageUrl', fullUrl)
        onSuccess?.({ url: fullUrl })
        message.success('上传成功')
      } else {
        throw new Error(res.message || '上传失败')
      }
    } catch (error) {
      onError?.(error as Error)
      message.error('上传失败')
    }
  }

  // ========== 公告管理 ==========
  const handleAddNotice = () => {
    setEditingNotice(null)
    noticeForm.resetFields()
    noticeForm.setFieldsValue({ sort: notices.length + 1, status: true })
    setNoticeModalVisible(true)
  }

  const handleEditNotice = (record: Notice) => {
    setEditingNotice(record)
    noticeForm.setFieldsValue(record)
    setNoticeModalVisible(true)
  }

  const handleDeleteNotice = (record: Notice) => {
    confirmDelete(`公告 #${record.id}`, '首页配置', async () => {
      const res = await homeConfigAPI.deleteNotice(record.id)
      if (res.code === 200) {
        setNotices(notices.filter(n => n.id !== record.id))
        message.success('删除成功')
        fetchNotices()
      }
    })
  }

  const handleToggleNoticeStatus = (record: Notice, status: boolean) => {
    confirmStatusChange(`公告 #${record.id}`, status, '首页配置', async () => {
      setNotices(notices.map(n => n.id === record.id ? { ...n, status } : n))
    })
  }

  const handleNoticeSubmit = async () => {
    try {
      const values = await noticeForm.validateFields()
      setNoticeSubmitting(true)

      if (editingNotice) {
        const res = await homeConfigAPI.updateNotice(editingNotice.id, values)
        if (res.code === 200) {
          setNotices(notices.map(n => n.id === editingNotice.id ? { ...n, ...values } : n))
          message.success('更新成功')
        }
      } else {
        const res = await homeConfigAPI.createNotice(values)
        if (res.code === 200) {
          setNotices([...notices, res.data])
          message.success('添加成功')
        }
      }
      setNoticeModalVisible(false)
      fetchNotices()
    } catch (error) {
      // 错误统一处理
    } finally {
      setNoticeSubmitting(false)
    }
  }

  // ========== 基本设置 ==========
  // ========== 基本设置 ==========
  const handleSettingsSave = async () => {
    try {
      const values = await settingsForm.validateFields()
      setSettingsSaving(true)
      const res = await homeConfigAPI.saveConfig(values)
      if (res.code === 200) {
        setSettings(values)
        message.success('设置保存成功')
      }
    } catch (error) {
      message.error('保存失败')
    } finally {
      setSettingsSaving(false)
    }
  }

  const handleSaveAll = () => {
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      message.success('所有配置已保存')
    }, 1000)
  }

  const bannerColumns: ColumnsType<Banner> = [
    { title: '排序', dataIndex: 'sort', key: 'sort', width: 80 },
    {
      title: '图片',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 150,
      render: (val: string) => val ? (
        <Image src={getImageUrl(val)} width={100} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} />
      ) : '-',
    },
    {
      title: '链接类型',
      dataIndex: 'linkType',
      key: 'linkType',
      width: 120,
      render: (val: string) => {
        const map: Record<string, string> = {
          product: '商品详情',
          category: '分类页面',
          page: '自定义页面',
          none: '无链接',
        }
        return <Tag>{map[val] || val}</Tag>
      },
    },
    { title: '链接值', dataIndex: 'linkValue', key: 'linkValue', width: 150, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (val: boolean, record: Banner) => (
        <Switch checked={val} size="small" onChange={(checked) => handleToggleBannerStatus(record, checked)} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: Banner) => (
        <Space size="small">
          <Button type="link" size="small" icon={<ArrowUpOutlined />} onClick={() => handleMoveBanner(record.id, 'up')} />
          <Button type="link" size="small" icon={<ArrowDownOutlined />} onClick={() => handleMoveBanner(record.id, 'down')} />
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditBanner(record)}>编辑</Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteBanner(record)}>删除</Button>
        </Space>
      ),
    },
  ]

  const noticeColumns: ColumnsType<Notice> = [
    { title: '排序', dataIndex: 'sort', key: 'sort', width: 80 },
    { title: '公告内容', dataIndex: 'content', key: 'content', ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (val: boolean, record: Notice) => (
        <Switch checked={val} size="small" onChange={(checked) => handleToggleNoticeStatus(record, checked)} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Notice) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditNotice(record)}>编辑</Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteNotice(record)}>删除</Button>
        </Space>
      ),
    },
  ]

  const productColumns: ColumnsType<RecommendProduct> = [
    { title: '排名', dataIndex: 'sort', key: 'sort', width: 80, render: (_: any, __: any, index: number) => index + 1 },
    {
      title: '商品图片',
      dataIndex: 'productImage',
      key: 'productImage',
      width: 100,
      render: (val: string) => val ? (
        <Image src={getImageUrl(val)} width={50} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} />
      ) : '-',
    },
    { title: '商品名称', dataIndex: 'productName', key: 'productName', width: 200 },
    { title: '价格', dataIndex: 'price', key: 'price', width: 120, render: (val: number) => `¥${val?.toFixed(2)}` },
  ]

  return (
    <div className="home-config-page">
      <Card size="small">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="轮播图配置" key="banner">
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddBanner}>
                添加轮播图
              </Button>
            </div>
            <Table
              columns={bannerColumns}
              dataSource={banners}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="公告配置" key="notice">
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNotice}>
                添加公告
              </Button>
            </div>
            <Table
              columns={noticeColumns}
              dataSource={notices}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="推荐商品" key="product">
            <div style={{ marginBottom: 16, padding: '8px 16px', background: '#f0f5ff', borderRadius: 4 }}>
              自动展示销量前6的商品（只读），根据实时销售数据自动更新
            </div>
            <Table
              columns={productColumns}
              dataSource={recommendProducts}
              rowKey="productId"
              pagination={false}
              size="small"
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="基本设置" key="settings">
            <Form form={settingsForm} layout="vertical" style={{ maxWidth: 500 }}>
              <Form.Item name="customerServicePhone" label="客服电话" rules={[
                { required: true, message: '请输入客服电话' },
              ]}>
                <Input placeholder="请输入客服电话" />
              </Form.Item>
              <Form.Item name="workTime" label="工作时间">
                <Input placeholder="如：周一至周五 9:00-18:00" />
              </Form.Item>
              <Form.Item name="maintenanceMode" label="维护模式" valuePropName="checked">
                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.maintenanceMode !== curr.maintenanceMode}>
                {({ getFieldValue }) => getFieldValue('maintenanceMode') && (
                  <Form.Item name="maintenanceMessage" label="维护提示语" rules={[
                    { required: true, message: '请输入维护提示语' },
                  ]}>
                    <TextArea rows={3} placeholder="系统维护中，请稍后再试" maxLength={100} showCount />
                  </Form.Item>
                )}
              </Form.Item>
              <Form.Item>
                <Button type="primary" onClick={handleSettingsSave} loading={settingsSaving}>保存设置</Button>
              </Form.Item>
            </Form>
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Button type="primary" size="large" icon={<SaveOutlined />} onClick={handleSaveAll} loading={loading}>
          保存所有配置
        </Button>
      </div>

      {/* 轮播图编辑弹窗 */}
      <Modal
        title={editingBanner ? '编辑轮播图' : '添加轮播图'}
        open={bannerModalVisible}
        onOk={handleBannerSubmit}
        onCancel={() => setBannerModalVisible(false)}
        confirmLoading={bannerSubmitting}
        width={500}
        destroyOnClose
      >
        <Form form={bannerForm} layout="vertical">
          <Form.Item name="imageUrl" label="轮播图片" rules={[{ required: true, message: '请上传图片' }]}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.imageUrl !== curr.imageUrl}>
                {() => {
                  const imageUrl = bannerForm.getFieldValue('imageUrl')
                  return imageUrl ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={getImageUrl(imageUrl)}
                        alt="轮播图"
                        style={{ width: 102, height: 102, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                      />
                      <Button
                        size="small"
                        danger
                        style={{ position: 'absolute', top: -10, right: -10, borderRadius: '50%' }}
                        onClick={() => bannerForm.setFieldValue('imageUrl', '')}
                      >
                        ×
                      </Button>
                    </div>
                  ) : null
                }}
              </Form.Item>
              <Upload
                customRequest={handleBannerUpload}
                listType="picture-card"
                accept="image/*"
                showUploadList={false}
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传图片</div>
                </div>
              </Upload>
            </div>
          </Form.Item>
          <Form.Item name="linkType" label="链接类型">
            <Select>
              <Option value="product">商品详情</Option>
              <Option value="category">分类页面</Option>
              <Option value="page">自定义页面</Option>
              <Option value="none">无链接</Option>
            </Select>
          </Form.Item>
          <Form.Item name="linkValue" label="链接值">
            <Input placeholder="如：商品ID、分类ID、页面路径" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sort" label="排序" rules={[{ required: true, message: '请输入排序值' }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="状态" valuePropName="checked">
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 公告编辑弹窗 */}
      <Modal
        title={editingNotice ? '编辑公告' : '添加公告'}
        open={noticeModalVisible}
        onOk={handleNoticeSubmit}
        onCancel={() => setNoticeModalVisible(false)}
        confirmLoading={noticeSubmitting}
        width={500}
        destroyOnClose
      >
        <Form form={noticeForm} layout="vertical">
          <Form.Item name="content" label="公告内容" rules={[
            { required: true, message: '请输入公告内容' },
            { max: 100, message: '公告内容不超过100字' },
          ]}>
            <TextArea rows={4} placeholder="请输入公告内容" maxLength={100} showCount />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sort" label="排序" rules={[{ required: true, message: '请输入排序值' }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="状态" valuePropName="checked">
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default HomeConfig
